import React from 'react';
import { FieldArray, useFormik, FormikProvider } from 'formik';
import { Button, Box } from '@qbe/ads-core';
import { FormCard } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';

import { FormStepContentProps } from 'types';
import { EmploymentFormFields, StepMethod, useFormState, EmploymentData } from 'contexts/FormProvider';
import { useScrollTop } from 'hooks/useScrollTop';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';

import EmploymentStatus from './sections/EmploymentStatus';
import EmploymentEdit from './sections/EmploymentEdit';
import EmploymentPreview from './sections/EmploymentPreview';
import validationSchema from './schema';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';

type DivRefs = { [key: string]: React.RefObject<HTMLDivElement> };

// Used formik for array component handling
const EmploymentForm = ({ onComplete }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const [mode, setMode] = React.useState<StepMethod>('Edit');
  const [selectedIndex, setSelectedIndex] = React.useState<string>('');

  const initialValues: EmploymentFormFields = {
    employmentData: formData?.employmentData ? formData.employmentData : [],
    employmentStatus: formData?.employmentStatus ? formData.employmentStatus : '',
  };

  const handleSubmit = (values: EmploymentFormFields) => {
    setFormCompleteEvent();
    onComplete({ ...formData, employmentData: values.employmentData, employmentStatus: values.employmentStatus });

    if (values.employmentStatus === 'no') {
      updateForm({
        employmentData: [],
      });
    }
  };

  const formikBag = useFormik<EmploymentFormFields>({
    initialValues,
    validationSchema,
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const isEdit = mode === 'Edit';
  const isDisabled = !validationSchema.isValidSync(formikBag.values);
  const isEmployed = formikBag.values.employmentStatus === 'yes';
  const employmentData = formikBag.values.employmentData;

  // Localized content constants
  const pageHeading = getTextContent('Employment.details.formHeading');
  const summaryHeading = getTextContent('Employment.details.summary');
  const submitLabel = getTextContent('Employment.details.submit.label');

  const refs: DivRefs = employmentData.reduce((accumulator: DivRefs, value: EmploymentData) => {
    accumulator[value.id] = React.createRef() as React.RefObject<HTMLDivElement>;
    return accumulator;
  }, {});

  React.useEffect(() => {
    if (!isEdit) window.scrollTo(0, 0);

    if (isEdit && selectedIndex && refs[selectedIndex].current?.offsetTop) {
      window.scrollTo(0, Number(refs[selectedIndex].current?.offsetTop));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, isEdit]);

  const handleEdit = (id: string) => {
    setMode('Edit');
    setSelectedIndex(id);
    setFormStartEvent();
  };

  return (
    <FormikProvider value={formikBag}>
      <form onSubmit={formikBag.handleSubmit}>
        {isEdit && (
          <>
            <FormCard variant="pageTitle" title={pageHeading} description="" />

            <FormSpacer type="pagesection" />

            <FieldArray name="employmentData">
              {(arrayHelpers) => (
                <>
                  <EmploymentStatus arrayHelpers={arrayHelpers} />

                  {employmentData.map((field, index) => (
                    <EmploymentEdit key={field.id} ref={refs[field.id]} index={index} arrayHelpers={arrayHelpers} />
                  ))}
                </>
              )}
            </FieldArray>
          </>
        )}

        {!isEdit && (
          <>
            <FormCard variant="pageTitle" title={summaryHeading} description="" />

            {employmentData.map((field) => (
              <EmploymentPreview key={field.id} field={field} onEdit={() => handleEdit(field.id)} />
            ))}
          </>
        )}

        {isEdit && isEmployed && (
          <>
            <FormSpacer type="pagesection" />
            <Box display="flex" justifyContent="center">
              <Button
                label={submitLabel}
                type="button"
                onClick={() => {
                  setMode('Preview');
                  setSelectedIndex('');
                }}
                disabled={isDisabled}
              />
            </Box>
          </>
        )}

        {!isEdit && (
          <>
            <FormSpacer type="pagesection" />
            <Box display="flex" justifyContent="center">
              <Button label={submitLabel} type="submit" disabled={isDisabled} />
            </Box>
          </>
        )}
      </form>
    </FormikProvider>
  );
};

export default EmploymentForm;
